import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';
import { 
  History, 
  Clock, 
  User, 
  Calendar, 
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Play,
  Pause,
  XCircle,
  FolderOpen,
  Settings,
  Plus,
  Trash2,
  ArrowLeft,
  Filter,
  Search
} from 'lucide-react';
import { useAuth } from '../AuthProvider';

interface ProjectActionHistory {
  id: number;
  project: string;
  action_type: string;
  action_type_display: string;
  description: string;
  old_value?: string;
  new_value?: string;
  performed_by: number;
  performed_by_name: string;
  created_at: string;
}

interface MobileHistoriqueProps {
  onClose: () => void;
}

const MobileHistorique: React.FC<MobileHistoriqueProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [history, setHistory] = useState<ProjectActionHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/projects-management/history/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      setHistory(data.history || []);
    } catch (err) {
      console.error('Erreur lors du chargement de l\'historique:', err);
      setError('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const refreshHistory = async () => {
    try {
      setRefreshing(true);
      await loadHistory();
    } catch (err) {
      console.error('Erreur lors du rafraîchissement:', err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (user?.isAdmin) {
      loadHistory();
    }
  }, [user?.isAdmin]);

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'created': return <Plus className="w-4 h-4 text-blue-500" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'reopened': return <Play className="w-4 h-4 text-blue-500" />;
      case 'status_changed': return <Settings className="w-4 h-4 text-orange-500" />;
      case 'step_created': return <Plus className="w-4 h-4 text-purple-500" />;
      case 'step_updated': return <Settings className="w-4 h-4 text-yellow-500" />;
      case 'step_deleted': return <Trash2 className="w-4 h-4 text-red-500" />;
      case 'service_added': return <Plus className="w-4 h-4 text-indigo-500" />;
      default: return <History className="w-4 h-4 text-gray-500" />;
    }
  };

  const getActionColor = (actionType: string) => {
    switch (actionType) {
      case 'created': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'reopened': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'status_changed': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'step_created': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'step_updated': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'step_deleted': return 'bg-red-100 text-red-800 border-red-200';
      case 'service_added': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredHistory = history.filter(action => {
    const matchesSearch = 
      String(action.project || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (action.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (action.performed_by_name || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = filterType === 'all' || action.action_type === filterType;

    return matchesSearch && matchesFilter;
  });

  if (!user?.isAdmin) {
    return (
      <div className="fixed inset-0 bg-gray-50 z-50 flex items-center justify-center">
        <div className="text-center p-6">
          <Settings className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Accès non autorisé</h2>
          <p className="text-gray-600 mb-4">Vous devez être administrateur pour accéder à cette page.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>
            <div className="flex items-center gap-2">
              <History className="w-6 h-6 text-indigo-600" />
              <h1 className="text-lg font-bold text-gray-900">Historique</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Filter className="w-5 h-5 text-gray-700" />
            </button>
            <button
              onClick={refreshHistory}
              disabled={refreshing}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 text-gray-700 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mt-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher dans l'historique..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 overflow-hidden"
            >
              <div className="flex gap-2 overflow-x-auto pb-2">
                {[
                  { value: 'all', label: 'Tout' },
                  { value: 'created', label: 'Créations' },
                  { value: 'completed', label: 'Terminés' },
                  { value: 'status_changed', label: 'Statuts' },
                  { value: 'step_created', label: 'Étapes' },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setFilterType(filter.value)}
                    className={`px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                      filterType === filter.value
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement de l'historique...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center px-4">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={loadHistory}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Réessayer
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="p-4">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
                  <div className="flex items-center gap-2">
                    <History className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="text-xs text-gray-600">Total</p>
                      <p className="text-lg font-bold text-gray-900">{history.length}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-xs text-gray-600">Terminés</p>
                      <p className="text-lg font-bold text-gray-900">
                        {history.filter(h => h.action_type === 'completed').length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="text-xs text-gray-600">Étapes</p>
                      <p className="text-lg font-bold text-gray-900">
                        {history.filter(h => h.action_type === 'step_created').length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
                  <div className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="text-xs text-gray-600">Modifs</p>
                      <p className="text-lg font-bold text-gray-900">
                        {history.filter(h => h.action_type === 'status_changed').length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* History List */}
              {filteredHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {searchTerm || filterType !== 'all' ? 'Aucun résultat' : 'Aucun historique'}
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm || filterType !== 'all' 
                      ? 'Essayez de modifier vos critères de recherche.'
                      : 'Les actions sur les projets apparaîtront ici.'
                    }
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredHistory.map((action, index) => (
                    <motion.div
                      key={action.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-1">
                          {getActionIcon(action.action_type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <FolderOpen className="w-3 h-3 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">
                                Projet #{action.project}
                              </span>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getActionColor(action.action_type)}`}>
                              {action.action_type_display}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-700 mb-3">{action.description}</p>
                          
                          {(action.old_value || action.new_value) && (
                            <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-2 mb-3">
                              {action.old_value && (
                                <div className="mb-1">
                                  <span className="font-medium">Ancien :</span> {action.old_value}
                                </div>
                              )}
                              {action.new_value && (
                                <div>
                                  <span className="font-medium">Nouveau :</span> {action.new_value}
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <div className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              <span>{action.performed_by_name}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>{formatDate(action.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileHistorique;
